import React, { useEffect } from 'react';
import { useWorkspacePicker } from 'workspaces/api';
import { Connection, Organization } from 'shared/types/ObjectTypes';
import ConfigurationsTab from '../flowMap/ConfigurationsTab';
import AddDestinationDrawer from '../flowMap/AddDestinationDrawer';
import TransformationRulesPanel from '../transformationRules/TransformationRulesPanel';
import CancelChangesConfirmationModal from '../CancelChangesConfirmationModal';
import { getRuleForDestination } from '../transformationRules/fieldMappingTypes';
import RouteGeneralSettings from './RouteGeneralSettings';
import { useAddDestinationDrawer } from './useAddDestinationDrawer';
import { useFlowMapActions } from './useFlowMapActions';
import { useRelatedRoutes } from './useRelatedRoutes';
import { useRouteRules } from './useRouteRules';
import { useRouteSourceFilters } from './useRouteSourceFilters';
import type { PendingEdits } from 'shared/components/configPanel/UnsavedSectionPrompt';
import type { PendingSetup } from './quickStart';
import type { RouteRecord } from './useRouteRecord';

type RouteConfigurationsTabProps = {
  record: RouteRecord;
  // The rules editor reports unsaved edits here, for the page's leave guard.
  pendingEditsRef: React.MutableRefObject<PendingEdits | null>;
  onNeedsSetupChange: (pendingSetup: PendingSetup) => void;
  onRulesPanelOpenChange: (open: boolean) => void;
  highlightAddDestination: boolean;
};

// The Configurations tab: the Route's name and workspace, the flow map, and
// the Add Destination drawer and Rules panel the flow map opens.
const RouteConfigurationsTab = ({
  record,
  pendingEditsRef,
  onNeedsSetupChange,
  onRulesPanelOpenChange,
  highlightAddDestination,
}: RouteConfigurationsTabProps) => {
  const {
    getToken,
    routeId,
    route,
    routeProvider,
    defaultRouteId,
    currentMemberIsAdmin,
    changesMade,
    setChangesMade,
    requestRouteReload,
    pageLoaded,
  } = record;

  const {
    organizations,
    loadingOrganizations,
    searchWorkspaces,
    clearWorkspaceSearch,
  } = useWorkspacePicker({ enabled: pageLoaded });
  const related = useRelatedRoutes(record);
  const rules = useRouteRules({
    record,
    reverseConfigBySite: related.reverseConfigBySite,
    storeReverseConfig: related.storeReverseConfig,
    onPanelOpenChange: onRulesPanelOpenChange,
  });
  const sourceFilters = useRouteSourceFilters({
    getToken,
    defaultRouteId,
    rulesPanelDestination: rules.rulesPanelDestination,
    rulesDirection: rules.rulesDirection,
    onDone: rules.handleCloseRules,
  });
  const actions = useFlowMapActions({
    record,
    reverseRoutesBySite: related.reverseRoutesBySite,
    refreshReverseRoutes: related.refreshReverseRoutes,
    routeConfigData: rules.routeConfigData,
    routeConfigurationName: rules.routeConfigurationName,
  });
  const drawer = useAddDestinationDrawer(record);

  // The Rules panel leaves with this tab, so the page's Help panel stops
  // showing the Route-rules guide.
  useEffect(
    () => () => onRulesPanelOpenChange(false),
    [onRulesPanelOpenChange],
  );
  // The Rules panel leaves with this tab, often in the same update that
  // discards its edits, so it never re-renders to report them gone. Without
  // this the page's leave guard would keep asking about edits that no longer
  // exist.
  useEffect(
    () => () => {
      pendingEditsRef.current = null;
    },
    [pendingEditsRef],
  );

  return (
    <>
      {actions.showConfirmationModal && (
        <CancelChangesConfirmationModal
          onToggleModal={actions.handleCancelNavigation}
          onConfirm={actions.handleConfirmNavigation}
        />
      )}
      <RouteGeneralSettings
        record={record}
        organizations={organizations}
        onSearchOrgs={searchWorkspaces}
        onLoadMoreOrgs={clearWorkspaceSearch}
      />
      <ConfigurationsTab
        route={route}
        routeProvider={routeProvider}
        token={getToken()}
        currentMemberIsAdmin={currentMemberIsAdmin}
        onTestAction={actions.handleTestAction}
        testActionLoading={actions.testActionLoading}
        testActionResponse={actions.testActionData}
        onSaveWebhook={actions.handleUpdateWebhookConfiguration}
        onCancelWebhook={actions.handleCancelWebhook}
        webhookChangesMade={changesMade}
        onWebhookChangesMade={() => setChangesMade(true)}
        reverseRoutesBySite={related.reverseRoutesBySite}
        providerCanReceive={
          !!routeProvider.type?.actions?.some((a: any) => a.type === 'push')
        }
        destinationsWithRules={rules.destinationsWithRules}
        reverseDestinationsWithRules={rules.reverseDestinationsWithRules}
        destinationsWithDeviceFilters={
          sourceFilters.destinationsWithDeviceFilters
        }
        getRule={(destId: string) =>
          getRuleForDestination(
            rules.routeConfigData,
            routeProvider?.id ?? '',
            destId,
          )
        }
        // The reverse flow's rule lives on its own route record, and its
        // provider is the site — the roles are swapped.
        getReverseRule={(destId: string) =>
          getRuleForDestination(
            related.reverseConfigBySite[destId]?.data,
            destId,
            routeProvider?.id ?? '',
          )
        }
        reverseRuleEditable={(destId: string) =>
          !!related.reverseConfigBySite[destId]?.routeId
        }
        providerSharedWith={related.providerSharedWith}
        providerAlsoProvidesFor={related.providerAlsoProvidesFor}
        onEnableReverseFlow={related.handleEnableReverseFlow}
        onDisableReverseFlow={related.handleDisableReverseFlow}
        onOpenRules={rules.handleOpenRules}
        getSourceFilter={sourceFilters.getSourceFilter}
        onRemoveSite={async (d: Connection) => {
          await actions.handleRemoveDestination(d);
          requestRouteReload();
        }}
        onAddDestination={drawer.open}
        onNeedsSetupChange={onNeedsSetupChange}
        onToggleRouteEnabled={actions.handleToggleProviderEnabled}
        routeUrlId={routeId}
        organizations={organizations}
        onLoadMoreOrgs={clearWorkspaceSearch}
        onSearchOrgs={searchWorkspaces}
        highlightAddDestination={highlightAddDestination}
      />
      <AddDestinationDrawer
        isOpen={drawer.isOpen}
        onClose={drawer.close}
        // Now that provider-side records are listed as destinations, the
        // Route's own provider can turn up here — attaching it would
        // point the Route at itself.
        candidates={drawer.candidates}
        loading={drawer.loading}
        onAttach={drawer.handleAttach}
        onCreateNew={drawer.handleCreateNew}
        onSearch={drawer.handleSearch}
        totalCount={drawer.totalCount}
        pageOffset={drawer.offset}
        onPageChange={drawer.handlePageClick}
        workspaceOptions={organizations
          .filter((o: Organization) => o.id)
          .map((o: Organization) => ({ label: o.name, value: o.id as string }))}
        workspaceFilter={drawer.workspaceFilter}
        onChangeWorkspaceFilter={drawer.handleWorkspaceChange}
        loadingWorkspaces={loadingOrganizations}
      />
      <TransformationRulesPanel
        isOpen={rules.rulesPanelOpen}
        onClose={rules.handleCloseRules}
        destination={rules.rulesTarget?.counterpart ?? null}
        providerId={rules.rulesTarget?.providerId ?? ''}
        currentRule={rules.currentPanelRule}
        currentConfigurationName={rules.rulesTarget?.configName ?? ''}
        onSave={rules.handleSaveRules}
        onDelete={rules.handleDeleteRule}
        currentSourceFilter={sourceFilters.currentSourceFilter}
        onSaveSourceFilter={sourceFilters.handleSaveSourceFilter}
        onDeleteSourceFilter={sourceFilters.handleDeleteSourceFilter}
        onLoadSourceFilterSources={sourceFilters.loadSourceFilterSources}
        initialEditor={rules.rulesInitialEditor}
        initialEditorNonce={rules.rulesEditorNonce}
        pendingEditsRef={pendingEditsRef}
      />
    </>
  );
};

export default RouteConfigurationsTab;
